import { yupResolver } from '@hookform/resolvers/yup';
import { FC, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from 'src/app/redux/createAction';
import { RequestStatus } from 'src/shared/api/types';
import { ButtonSquared } from 'src/shared/ui/_buttons/ButtonSquared';
import { PasswordField } from 'src/shared/ui/_form_fields/PasswordField';
import { StringField } from 'src/shared/ui/_form_fields/StringField';
import { ButtonsWrapper } from 'src/shared/ui/_layout/ButtonsWrapper';
import { Preloader } from 'src/shared/ui/_loaders/Preloader';
import { ModalLegacy } from 'src/shared/ui/_modals/ModalLegacy';
import * as Yup from 'yup';
import { actionsAuth } from '../../_BLL/slice';
import { LoginValues } from '../../_BLL/types';
import { MFAForm } from '../MFAForm';
import { ResetPasswordForm } from '../ResetPasswordForm';
import s from './SignInForm.module.scss';

export const SignInForm: FC = () => {
	// * Selectors
	const status = useAppSelector((state) => state.auth.status);
	const mfa = useAppSelector((state) => state.auth.mfa);

	// * Actions
	const dispatch = useAppDispatch();
	const { login, clearResetStatus } = actionsAuth;

	// * Form
	const schema = Yup.object().shape({
		username: Yup.string().required(),
		password: Yup.string().required(),
	});

	const formMethods = useForm({
		defaultValues: {
			username: '',
			password: '',
		},
		resolver: yupResolver(schema),
	});

	const { handleSubmit, control, watch } = formMethods;

	const userName = watch('username');

	const onSubmit = (values: LoginValues) => {
		dispatch(login(values));
	};

	// * Reset password
	const [openResetPassword, setOpenResetPassword] = useState(false);

	const toggleResetPasswordModal = () => {
		setOpenResetPassword((prevState) => !prevState);
		dispatch(clearResetStatus());
	};

	// * Conditions
	const isLoading = status === RequestStatus.loading;

	// * Render
	return (
		<>
			<ModalLegacy
				show={openResetPassword}
				toggleModal={toggleResetPasswordModal}
				Content={<ResetPasswordForm userName={userName} />}
				width={20}
			/>

			{mfa ? (
				<MFAForm userName={userName} />
			) : (
				<form
					className={s.form}
					onSubmit={handleSubmit(onSubmit)}
				>
					<FormProvider {...formMethods}>
						<StringField
							dataTest="username"
							name="username"
							label="User name or email"
						/>

						<PasswordField
							data-test="password"
							name="password"
							label="Password"
						/>

						{isLoading ? (
							<div className={s.preloaderWrapper}>
								<Preloader />
							</div>
						) : (
							<ButtonsWrapper direction="column">
								<ButtonSquared
									data-test="signIn"
									type="submit"
								>
									sign in
								</ButtonSquared>

								<a
									href="https://www.denominator.com/contact-us/"
									target="_blank"
									rel="noreferrer"
									style={{ textDecoration: 'none' }}
								>
									<ButtonSquared>register</ButtonSquared>
								</a>

								<ButtonSquared
									onClick={toggleResetPasswordModal}
									status={status}
								>
									forgot password
								</ButtonSquared>
							</ButtonsWrapper>
						)}
					</FormProvider>
				</form>
			)}
		</>
	);
};
